import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { MobileShell } from "@/components/MobileShell";
import { CheckCircle2, Link2 } from "lucide-react";
import { toast } from "sonner";

export default function OrderSuccess() {
    const { id } = useParams();
    const [order, setOrder] = useState(null);
    const settings = useSettings();

    const copyLink = () => {
        const url = `${window.location.origin}/order/${id}`;
        if (navigator.clipboard) {
            navigator.clipboard.writeText(url).then(() => toast.success("Link copied!"));
        } else {
            const el = document.createElement("textarea");
            el.value = url;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);
            toast.success("Link copied!");
        }
    };

    useEffect(() => {
        api.get(`/orders/r/${id}`).then((r) => setOrder(r.data)).catch(() => setOrder(false));
    }, [id]);

    return (
        <MobileShell showCart={false}>
            <div className="pt-12 text-center animate-fadeUp" data-testid="order-success-screen">
                <div className="mx-auto w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <CheckCircle2 className="h-10 w-10 text-primary" strokeWidth={1.5} />
                </div>
                <h1 className="font-serif text-4xl mt-6">Thank you!</h1>
                <p className="text-muted-foreground mt-2 text-sm">
                    Your order has been received.
                </p>
                {order && order !== false && (
                    <div className="mt-6 bg-surface/40 border border-border/50 rounded-2xl p-5 text-left">
                        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                            Order
                        </p>
                        <p
                            data-testid="order-number"
                            className="font-serif text-2xl text-foreground"
                        >
                            {order.order_number}
                        </p>
                        <div className="mt-4 space-y-1 text-sm">
                            {order.items.map((it, idx) => (
                                <div key={idx} className="flex justify-between">
                                    <span>
                                        {it.quantity} × {" "}
                                        {it.product_id ? (
                                            <Link
                                                to={`/product/${it.product_id}`}
                                                className="underline decoration-border/70 underline-offset-2 hover:text-primary transition-colors"
                                            >
                                                {it.name}
                                            </Link>
                                        ) : (
                                            it.name
                                        )}
                                    </span>
                                    <span>₹{(it.price * it.quantity).toFixed(2)}</span>
                                </div>
                            ))}
                        </div>
                        {order.promo_code && (
                            <div className="mt-3 pt-3 border-t border-border/40 space-y-1 text-sm" data-testid="order-success-promo">
                                <div className="flex justify-between text-muted-foreground">
                                    <span>Subtotal</span>
                                    <span>₹{Number(order.subtotal || 0).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-moss">
                                    <span>
                                        Promo {order.promo_code}
                                        {order.promo_percent != null && (
                                            <> · {Number(order.promo_percent).toFixed(0)}%</>
                                        )}
                                    </span>
                                    <span>-₹{Number(order.discount_amount || 0).toFixed(2)}</span>
                                </div>
                            </div>
                        )}
                        <div className="flex justify-between mt-3 pt-3 border-t border-border/40">
                            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                                Total
                            </span>
                            <span className="font-serif text-xl text-primary font-semibold">
                                ₹{order.total.toFixed(2)}
                            </span>
                        </div>
                        <div className="mt-3 text-xs text-muted-foreground">
                            Status: <span className="text-foreground capitalize">{order.status}</span>
                        </div>
                        <div className="mt-4 pt-4 border-t border-border/40 space-y-2 text-sm" data-testid="order-delivery-details">
                            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Delivery details</p>
                            {order.customer_name && (
                                <div className="flex justify-between gap-2">
                                    <span className="text-muted-foreground shrink-0">Name</span>
                                    <span className="text-right">{order.customer_name}</span>
                                </div>
                            )}
                            {order.customer_phone && (
                                <div className="flex justify-between gap-2">
                                    <span className="text-muted-foreground shrink-0">Contact No</span>
                                    <span className="text-right">{order.customer_phone}</span>
                                </div>
                            )}                            
                            {order.customer_address && (
                                <div className="flex justify-between gap-2">
                                    <span className="text-muted-foreground shrink-0">Address</span>
                                    <span className="text-right whitespace-pre-line">
                                        {String(order.customer_address).replace(/\r\n|\r/g, "\n")}
                                    </span>
                                </div>
                            )}
                            {order.sender_detail && (
                              <div className="flex justify-between gap-2">
                                <span className="text-muted-foreground shrink-0">Sender Name</span>
                                <span className="text-right">{order.sender_detail}</span>
                              </div>
                            )}
                            {order.notes && (
                                <div className="flex justify-between gap-2">
                                    <span className="text-muted-foreground shrink-0">Notes</span>
                                    <span className="text-right">{order.notes}</span>
                                </div>
                            )}
                        </div>
                        {settings.whats_app_number && (
                            <div className="mt-4 rounded-2xl border border-[#25D366]/30 p-3">
                                <p className="text-xs text-[#25D366] font-medium text-center mb-2">
                                    📲 Share your order link to track status anytime
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={copyLink}
                                        className="flex-1 flex items-center justify-center gap-1.5 rounded-full border border-border/60 px-3 py-2 text-sm hover:border-foreground transition-colors"
                                    >
                                        <Link2 className="h-4 w-4" />
                                        Copy link
                                    </button>
                                    <a
                                        href={`https://wa.me/${settings.whats_app_number}?text=${encodeURIComponent(
                                            (settings.whats_app_share_message ? settings.whats_app_share_message + " " : "") +
                                            `${window.location.origin}/order/${id}`
                                        )}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-[#25D366] text-white px-3 py-2 text-sm font-medium hover:bg-[#1ebe5d] transition-colors"
                                    >
                                        <svg className="h-4 w-4 fill-current shrink-0" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                        WhatsApp
                                    </a>
                                </div>
                            </div>
                        )}
                        {!settings.whats_app_number && (
                            <button
                                onClick={copyLink}
                                className="mt-4 w-full flex items-center justify-center gap-2 rounded-full border border-border/60 px-4 py-2 text-sm hover:border-foreground transition-colors"
                            >
                                <Link2 className="h-4 w-4" />
                                Copy order link
                            </button>
                        )}
                    </div>
                )}
                <Link
                    to="/"
                    data-testid="continue-shopping"
                    className="mt-8 inline-block rounded-full bg-foreground text-background px-8 py-3 hover:bg-primary transition-colors"
                >
                    Continue shopping
                </Link>
            </div>
        </MobileShell>
    );
}
